/**
 * W1-11a (app-registry-keymap): the keymap service implementation (ARCHITECTURE.md §6.3, DESIGN.md §8).
 * `keymap.ts` wires the app singleton; this file has no dependency on the module glob (unit-testable).
 *
 * Effective bindings = assemble(preset, overrides, altFree, os):
 *   1. every module's `keybindings[preset]` in module order (a module without an `rpe` list shares its `default`
 *      list: "everything else is shared, so switching presets costs nothing");
 *   2. user overrides with `remove: true` delete the preset binding with the same command, condition and chord
 *      (the declared chord or its Alt-free / Mac replacement);
 *   3. Alt-free variant: each Alt chord of the PRESET is replaced by its `altFree` chord or unbound (null/absent);
 *   4. macOS: a declared `mac` chord replaces the chord (null unbinds); a chord in RESERVED_CHORDS_MAC without
 *      one is unbound; `Ctrl` means Cmd;
 *   5. user additions are appended (they win ties against the preset); an addition on a chord refused on this OS
 *      (reserved, Ctrl+Alt) is left out with a 'reserved' / 'ctrlAlt' issue, whoever wrote it (recorder, JSON
 *      import, hand-edited or synced settings, `setOverrides`).
 * Resolution: among bindings of the chord's trigger whose condition (binding `when` && command `when`) holds, the
 * most specific wins; ties go to user overrides, then to the first preset binding. A command's `enabled()` is not
 * consulted: a key whose command is disabled still resolves to it, so the registry can show the reason (DESIGN.md
 * §8.2: "disabled with a reason") instead of the key silently falling through to a less specific binding.
 * Conflicts: same trigger, different action, contexts that can overlap with equal specificity -> red; otherwise ->
 * yellow.
 * Settings are the single source of truth (settings.keyboard.preset / altFree / overrides).
 * JSON import replaces the overrides with the file's valid entries, except that a file none of whose entries can be
 * used (e.g. commands of another version) changes nothing. Issues carry a translatable `text` for the keymap editor.
 */

import type { Json, MessageRef, Unsubscribe } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import type { SettingsService } from '../settings/types';
import {
  chordOfEvent,
  eventChords,
  FUNCTION_CODES,
  NAMED_CODES,
  NUMPAD_CODES,
  normalizeChord,
  parseChord,
  pointerModifiers,
  triggerOf,
  wheelChord,
  type Os,
  type PointerModifiers,
} from './chords';
import { createLayoutLabels, type LayoutLabels } from './layoutLabels';
import { m } from './registry.messages';
import { checkChord, isMacReserved, isRefused, isReserved, type ChordCheck } from './reserved';
import { translate as defaultTranslate, ref, translateRef, type Params, type Translate } from './text';
import type {
  Chord,
  CommandDef,
  CommandId,
  FeatureModule,
  KeyBinding,
  KeymapPresetId,
  KeymapService,
  WhenContext,
} from './types';
import {
  andWhen,
  BUILTIN_ATOM_SET,
  compileAst,
  compileWhen,
  whenOverlap,
  whenRequires,
  type CompiledWhen,
  type FixedComparisons,
} from './when';

export const KEYMAP_PRESETS: readonly KeymapPresetId[] = ['default', 'rpe'];

/** One effective binding with where it came from. */
export interface KeymapEntry {
  /** The binding as it applies now (chord after the Alt-free and macOS transforms, canonical). */
  readonly binding: KeyBinding;
  /** The binding as declared by the module or the user. */
  readonly declared: KeyBinding;
  readonly source: 'preset' | 'override';
  /** Contributing module (null for user overrides). */
  readonly moduleId: string | null;
  /** Position in the effective list (stable). */
  readonly order: number;
  /** Canonical chord without `hold:`. */
  readonly trigger: Chord;
  /** Combined binding + command condition; null when the condition is invalid (the binding never applies). */
  readonly when: CompiledWhen | null;
}

export type KeymapIssueCode =
  | 'invalidChord'
  | 'invalidWhen'
  | 'unknownCommand'
  | 'reserved'
  | 'ctrlAlt'
  | 'altFreeMissing'
  | 'macMissing'
  | 'macShiftWheel'
  | 'badAlternate'
  | 'removeInPreset'
  | 'unreliable'
  | 'conflict';

export interface KeymapIssue {
  readonly level: 'error' | 'warning';
  readonly code: KeymapIssueCode;
  /** Developer text (console, startup audit test); English, not shown in the UI. */
  readonly message: string;
  /**
   * The same problem for people (keymap editor: `i18n.tr(issue.text)`). Its `chord` and `command` params are the
   * shortcut label and command title in the language and keyboard layout current when the keymap was assembled.
   */
  readonly text: MessageRef;
  readonly preset?: KeymapPresetId;
  readonly os?: Os;
  readonly altFree?: boolean;
  readonly moduleId?: string | null;
  readonly binding?: KeyBinding;
  readonly with?: KeyBinding;
}

export interface KeymapDeps {
  /** Modules contributing keybindings (the registry's modules). */
  readonly modules: () => readonly FeatureModule[];
  /** Command lookup (for command `when` conditions and import validation); omitted = not checked. */
  readonly command?: (id: CommandId) => CommandDef | undefined;
  /** Known when-atom names (built-in + registered); default: built-in only. */
  readonly whenAtomNames?: () => ReadonlySet<string>;
  /** Settings holding keyboard.preset / altFree / overrides. */
  readonly settings: Pick<SettingsService, 'value' | 'set' | 'subscribe'>;
  readonly os?: Os;
  readonly labels?: LayoutLabels;
  readonly translate?: Translate;
  /** Subscribe to changes of the sources (modules attached, atoms registered). */
  readonly onSourcesChange?: (cb: () => void) => Unsubscribe;
}

/** The keymap service plus the helpers the router, the recorder (W2-13) and the startup audit use. */
export interface AppKeymap extends KeymapService {
  readonly os: Os;
  /** Every binding that applies to a chord in this context, most specific first. */
  candidates(chord: Chord, ctx: WhenContext): readonly KeymapEntry[];
  /** Is anything bound to this chord's trigger in any context? (Cheap pre-check before building a context.) */
  hasBindings(chord: Chord | null): boolean;
  /** Effective bindings with their origin. */
  entries(): readonly KeymapEntry[];
  /** Both candidate chords of a keyboard event (physical code, produced character). */
  eventChords(e: KeyboardEvent): { readonly code: Chord | null; readonly char: Chord | null };
  wheelChord(e: WheelEvent): Chord;
  /** Click/drag modifiers (Cmd+click toggles on macOS, Ctrl+click is a right-click there; no-snap per Alt-free). */
  pointerModifiers(e: MouseEvent): PointerModifiers;
  /** Recorder rule for a chord (reserved / Ctrl+Alt refused, unreliable warned), with a layout-aware label. */
  check(chord: string): ChordCheck;
  /** Translated reason of a check (empty for 'ok'). */
  explain(check: ChordCheck): string;
  /** Canonical form of a chord, or null. */
  normalize(chord: string): Chord | null;
  /** Does the binding's condition only hold when `atom` holds (e.g. it explicitly targets dialogs)? */
  requires(entry: KeymapEntry, atom: string): boolean;
  /** Problems found while assembling the current keymap (invalid chords or conditions in modules or overrides). */
  issues(): readonly KeymapIssue[];
  dispose(): void;
}

// ---------------------------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------------------------

/** Condition text normalised for identity comparisons (`remove` matching). */
export function whenKey(when: string | undefined): string {
  return when === undefined ? '' : when.replace(/\s+/g, ' ').trim();
}

/** Stable JSON text for args comparisons. */
function argsKey(args: Json | undefined): string {
  return args === undefined ? '' : stableJson(args);
}

function stableJson(v: Json): string {
  if (Array.isArray(v)) return `[${v.map((x: Json) => stableJson(x)).join(',')}]`;
  if (v !== null && typeof v === 'object') {
    const o = v as { readonly [k: string]: Json };
    return `{${Object.keys(o)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableJson(o[k] as Json)}`)
      .join(',')}}`;
  }
  return JSON.stringify(v);
}

function sameAction(a: KeyBinding, b: KeyBinding): boolean {
  return a.command === b.command && argsKey(a.args) === argsKey(b.args);
}

/** Binding with a new chord (keeps every other field). */
function withChord(b: KeyBinding, chord: Chord): KeyBinding {
  return b.chord === chord ? b : { ...b, chord };
}

/**
 * Chord of a preset binding after the Alt-free and macOS transforms, or null when it is unbound there.
 * The Alt-free variant applies to the declared chord. On macOS a declared `mac` chord always replaces it (null
 * unbinds; required for chords in RESERVED_CHORDS_MAC, also used for chords macOS takes without being listed, such
 * as Cmd+Shift+`), and a chord reserved on macOS without an alternate is unbound. A `mac` alternate is never
 * Alt-free-transformed (it is the module's deliberate choice for macOS, where Option chords are safe).
 */
export function transformPresetChord(b: KeyBinding, altFree: boolean, os: Os): Chord | null {
  let chord = normalizeChord(b.chord);
  if (chord === null) return null;
  if (altFree && parseChord(chord)?.alt === true) {
    if (b.altFree === undefined || b.altFree === null) return null;
    chord = normalizeChord(b.altFree);
    if (chord === null || parseChord(chord)?.alt === true) return null;
  }
  if (os === 'mac') {
    if (b.mac === null) return null;
    if (b.mac !== undefined) {
      const mac = normalizeChord(b.mac);
      if (mac === null || isReserved(mac, 'mac')) return null;
      chord = mac;
    } else if (isReserved(chord, 'mac')) return null;
  }
  return chord;
}

/** Every chord a preset binding is known by (declared, Alt-free, Mac), canonical. */
function aliasesOf(b: KeyBinding): Set<Chord> {
  const out = new Set<Chord>();
  for (const c of [b.chord, b.altFree, b.mac]) {
    if (typeof c === 'string') {
      const n = normalizeChord(c);
      if (n !== null) out.add(n);
    }
  }
  return out;
}

interface AssembleInput {
  readonly modules: readonly FeatureModule[];
  readonly preset: KeymapPresetId;
  readonly altFree: boolean;
  readonly os: Os;
  readonly overrides: readonly KeyBinding[];
  readonly compile: (b: KeyBinding) => CompiledWhen | { readonly error: string };
  /** Display names for issue texts (default: the chord and the command id as written). */
  readonly names?: IssueNames;
}

/** How issue texts name shortcuts and commands. */
export interface IssueNames {
  readonly chord: (chord: string) => string;
  readonly command: (id: CommandId) => string;
}

const RAW_NAMES: IssueNames = { chord: (c) => c, command: (id) => id };

/** Translatable text of an issue about a binding (`chord` and `command` filled from the binding). */
function issueText(names: IssueNames, key: MessageKey, b: KeyBinding, params: Params = {}): MessageRef {
  const chord = normalizeChord(b.chord);
  return ref(key, {
    chord: chord === null ? b.chord : names.chord(chord),
    command: names.command(b.command),
    ...params,
  });
}

/** The preset list of a module (an absent `rpe` list shares the `default` one). */
export function moduleBindings(mod: FeatureModule, preset: KeymapPresetId): readonly KeyBinding[] {
  const kb = mod.keybindings;
  if (kb === undefined) return [];
  return kb[preset] ?? (preset === 'default' ? [] : (kb.default ?? []));
}

/** Pure assembly of the effective bindings. */
export function assembleKeymap(input: AssembleInput): { entries: KeymapEntry[]; issues: KeymapIssue[] } {
  const issues: KeymapIssue[] = [];
  const names = input.names ?? RAW_NAMES;
  const ctx = { preset: input.preset, os: input.os, altFree: input.altFree };
  const removes = input.overrides.filter((o) => o.remove === true);
  const isRemoved = (b: KeyBinding): boolean =>
    removes.some((r) => {
      if (r.command !== b.command || whenKey(r.when) !== whenKey(b.when)) return false;
      const chord = normalizeChord(r.chord);
      return chord !== null && aliasesOf(b).has(chord);
    });

  const entries: KeymapEntry[] = [];
  const push = (
    binding: KeyBinding,
    declared: KeyBinding,
    source: KeymapEntry['source'],
    moduleId: string | null,
  ): void => {
    const trigger = triggerOf(binding.chord);
    if (trigger === null) return;
    const compiled = input.compile(binding);
    if ('error' in compiled) {
      issues.push({
        level: 'error',
        code: 'invalidWhen',
        message: `${declared.chord} -> ${declared.command}: invalid when "${declared.when ?? ''}": ${compiled.error}`,
        text: issueText(names, m.issueInvalidWhen, declared, { error: compiled.error }),
        ...ctx,
        moduleId,
        binding: declared,
      });
    }
    entries.push({
      binding,
      declared,
      source,
      moduleId,
      order: entries.length,
      trigger,
      when: 'error' in compiled ? null : compiled,
    });
  };

  for (const mod of input.modules) {
    for (const b of moduleBindings(mod, input.preset)) {
      if (b.remove === true) {
        issues.push({
          level: 'error',
          code: 'removeInPreset',
          message: `preset binding with remove: ${b.chord}`,
          text: issueText(names, m.issueRemoveInPreset, b),
          ...ctx,
          moduleId: mod.id,
          binding: b,
        });
        continue;
      }
      if (normalizeChord(b.chord) === null) {
        issues.push({
          level: 'error',
          code: 'invalidChord',
          message: `invalid chord "${b.chord}"`,
          text: ref(m.issueInvalidChord, { chord: b.chord }),
          ...ctx,
          moduleId: mod.id,
          binding: b,
        });
        continue;
      }
      if (isRemoved(b)) continue;
      const chord = transformPresetChord(b, input.altFree, input.os);
      if (chord !== null) push(withChord(b, chord), b, 'preset', mod.id);
    }
  }
  for (const o of input.overrides) {
    if (o.remove === true) continue;
    const chord = normalizeChord(o.chord);
    if (chord === null) {
      issues.push({
        level: 'error',
        code: 'invalidChord',
        message: `invalid chord "${o.chord}"`,
        text: ref(m.issueInvalidChord, { chord: o.chord }),
        ...ctx,
        moduleId: null,
        binding: o,
      });
      continue;
    }
    // Hand-edited, synced or programmatic overrides skip the recorder: a chord the browser or the OS keeps (on this
    // OS) or a Ctrl+Alt (AltGr) chord would be a dead binding, so it is reported for the keymap editor and left out.
    const refused = checkChord(chord, input.os);
    if (refused.status === 'reserved' || refused.status === 'ctrlAlt') {
      const reserved = refused.status === 'reserved';
      issues.push({
        level: 'error',
        code: refused.status,
        message: `${reserved ? 'reserved' : 'Ctrl+Alt'} chord in override: ${o.chord} -> ${o.command}`,
        text: issueText(names, reserved ? m.issueReserved : m.issueCtrlAlt, o),
        ...ctx,
        moduleId: null,
        binding: o,
      });
      continue;
    }
    push(withChord(o, chord), o, 'override', null);
  }
  return { entries, issues };
}

/** Most specific first; ties: user overrides (latest first), then presets in declaration order. */
function compareEntries(a: KeymapEntry, b: KeymapEntry): number {
  const sa = a.when?.specificity ?? 0;
  const sb = b.when?.specificity ?? 0;
  if (sa !== sb) return sb - sa;
  if (a.source !== b.source) return a.source === 'override' ? -1 : 1;
  return a.source === 'override' ? b.order - a.order : a.order - b.order;
}

/** Red/yellow relation between two bindings of the same trigger (null: same action, no conflict). */
export function conflictLevel(
  a: { readonly binding: KeyBinding; readonly when: CompiledWhen },
  b: { readonly binding: KeyBinding; readonly when: CompiledWhen },
  fixed: FixedComparisons,
): 'red' | 'yellow' | null {
  if (sameAction(a.binding, b.binding)) return null;
  const overlap = whenOverlap(a.when.ast, b.when.ast, fixed);
  return overlap && a.when.specificity === b.when.specificity ? 'red' : 'yellow';
}

// ---------------------------------------------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------------------------------------------

const KEYMAP_FORMAT = 'phiedit-keymap';
const KEYMAP_VERSION = 1;

function isRecord(v: unknown): v is Readonly<Record<string, unknown>> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isJsonValue(v: unknown): v is Json {
  if (v === null || typeof v === 'boolean' || typeof v === 'string') return true;
  if (typeof v === 'number') return Number.isFinite(v);
  if (Array.isArray(v)) return v.every(isJsonValue);
  return isRecord(v) && Object.values(v).every(isJsonValue);
}

export function createKeymapService(deps: KeymapDeps): AppKeymap {
  const os: Os = deps.os ?? 'other';
  const t = deps.translate ?? defaultTranslate;
  const labels = deps.labels ?? createLayoutLabels();
  const listeners = new Set<() => void>();
  const atomNames = (): ReadonlySet<string> => deps.whenAtomNames?.() ?? BUILTIN_ATOM_SET;

  /** Assembled keymap; results keep their identity until the keymap changes (safe for useSyncExternalStore). */
  let cache: {
    readonly entries: readonly KeymapEntry[];
    readonly bindings: readonly KeyBinding[];
    readonly byTrigger: ReadonlyMap<Chord, readonly KeymapEntry[]>;
    readonly byCommand: Map<CommandId, readonly Chord[]>;
    readonly issues: readonly KeymapIssue[];
    readonly atoms: ReadonlySet<string>;
  } | null = null;
  /** Compiled conditions by (binding when, command when) text; cleared when the atom names change. */
  let whenCache = new Map<string, CompiledWhen | { readonly error: string }>();
  let atomsSeen: ReadonlySet<string> | null = null;

  const notify = (): void => {
    for (const cb of [...listeners]) cb();
  };
  const invalidate = (): void => {
    cache = null;
    notify();
  };

  const keyboard = (): { preset: KeymapPresetId; altFree: boolean; overrides: readonly KeyBinding[] } =>
    deps.settings.value.keyboard;

  /** Binding condition && command condition. */
  const compileFor = (b: KeyBinding): CompiledWhen | { readonly error: string } => {
    const known = atomNames();
    if (known !== atomsSeen) {
      atomsSeen = known;
      whenCache = new Map();
    }
    const cmdWhen = deps.command?.(b.command)?.when;
    const cacheKey = `${whenKey(b.when)}\u0000${whenKey(cmdWhen)}`;
    const hit = whenCache.get(cacheKey);
    if (hit !== undefined) return hit;
    const own = compileWhen(b.when ?? '', known, t);
    let result: CompiledWhen | { readonly error: string };
    if ('error' in own) result = own;
    else if (cmdWhen === undefined) result = own;
    else {
      const cmd = compileWhen(cmdWhen, known, t);
      result = 'error' in cmd ? cmd : compileAst(andWhen(own.ast, cmd.ast), `${b.when ?? ''} && ${cmdWhen}`);
    }
    whenCache.set(cacheKey, result);
    return result;
  };

  const ensure = (): NonNullable<typeof cache> => {
    const atoms = atomNames();
    if (cache?.atoms === atoms) return cache;
    const kb = keyboard();
    const { entries, issues } = assembleKeymap({
      modules: deps.modules(),
      preset: kb.preset,
      altFree: kb.altFree,
      os,
      overrides: kb.overrides,
      compile: compileFor,
      names: issueNames,
    });
    const byTrigger = new Map<Chord, KeymapEntry[]>();
    for (const e of entries) {
      const list = byTrigger.get(e.trigger) ?? [];
      list.push(e);
      byTrigger.set(e.trigger, list);
    }
    if (deps.command !== undefined) {
      for (const e of entries) {
        if (deps.command(e.binding.command) === undefined) {
          issues.push({
            level: 'error',
            code: 'unknownCommand',
            message: `unknown command "${e.binding.command}"`,
            text: issueText(issueNames, m.issueUnknownCommand, e.binding),
            moduleId: e.moduleId,
            binding: e.declared,
          });
        }
      }
    }
    cache = { entries, bindings: entries.map((e) => e.binding), byTrigger, byCommand: new Map(), issues, atoms };
    return cache;
  };

  const fixed = (): FixedComparisons => ({ preset: keyboard().preset, os });

  let lastKeyboard = deps.settings.value.keyboard;
  const unsubSettings = deps.settings.subscribe((s) => {
    if (s.keyboard === lastKeyboard) return;
    lastKeyboard = s.keyboard;
    invalidate();
  });
  const unsubSources = deps.onSourcesChange?.(invalidate);
  const unsubLabels = labels.subscribe(notify);

  /** Layout-aware label; `physicalSuffix: false` omits "(physical)" (inside sentences). */
  const formatLabel = (chord: Chord, physicalSuffix: boolean): string => {
    const p = parseChord(chord);
    if (p === null) return chord;
    const parts: string[] = [];
    if (p.ctrl) parts.push(t(os === 'mac' ? m.modCmd : m.modCtrl));
    if (p.alt) parts.push(t(os === 'mac' ? m.modOption : m.modAlt));
    if (p.shift) parts.push(t(m.modShift));
    let physical = false;
    if (p.kind === 'wheel') parts.push(t(m.keyWheel));
    else if (p.kind === 'char') parts.push(p.key);
    else if (NAMED_CODES.has(p.key)) parts.push(t(`registry.key${p.key}`));
    else if (FUNCTION_CODES.has(p.key)) parts.push(p.key);
    else if (NUMPAD_CODES.has(p.key)) parts.push(t(m.keyNumpad, { key: numpadChar(p.key) }));
    else {
      const l = labels.labelFor(p.key);
      parts.push(l?.text ?? p.key);
      physical = l?.physical ?? false;
    }
    let text = parts.join('+');
    if (physical && physicalSuffix) text = t(m.labelPhysical, { label: text });
    if (p.hold) text = t(m.labelHold, { label: text });
    return text;
  };

  const label = (chord: Chord): string => formatLabel(chord, true);
  const check = (chord: string): ChordCheck => checkChord(chord, os, (c) => formatLabel(c, false));
  /** Issue texts name shortcuts by their label and commands by their title. */
  const issueNames: IssueNames = {
    chord: (c) => formatLabel(c, false),
    command: (id) => {
      const title = deps.command?.(id)?.title;
      return title === undefined ? id : t(title);
    },
  };

  const candidates = (chord: Chord, ctx: WhenContext): KeymapEntry[] => {
    const trigger = triggerOf(chord);
    if (trigger === null) return [];
    const list = ensure().byTrigger.get(trigger) ?? [];
    return list.filter((e) => e.when !== null && e.when.eval(ctx)).sort(compareEntries);
  };

  const service: AppKeymap = {
    os,
    get preset() {
      return keyboard().preset;
    },
    get altFree() {
      return keyboard().altFree;
    },
    setPreset(p) {
      deps.settings.set('keyboard', 'preset', p);
    },
    setAltFree(on) {
      deps.settings.set('keyboard', 'altFree', on);
    },
    bindings: () => ensure().bindings,
    bindingsFor(command) {
      const c = ensure();
      let chords = c.byCommand.get(command);
      if (chords === undefined) {
        chords = c.entries.filter((e) => e.binding.command === command).map((e) => e.binding.chord);
        c.byCommand.set(command, chords);
      }
      return chords;
    },
    label,
    chordOf: (e) => chordOfEvent(e, os),
    /**
     * "Enabled" here means effective and applicable: the binding is in the assembled keymap (not removed by an
     * override, not unbound by the Alt-free or macOS transforms, valid chord and condition) and its condition (binding
     * `when` && command `when`) holds in `ctx`. The command's `enabled()` is deliberately not consulted: the most
     * specific applicable binding wins even when its command is disabled, and running it shows the reason.
     */
    resolve(chord, ctx) {
      return candidates(chord, ctx)[0]?.binding ?? null;
    },
    conflicts(b) {
      if (b.remove === true) return [];
      const trigger = triggerOf(b.chord);
      if (trigger === null) return [];
      const own = compileFor(b);
      if ('error' in own) return [];
      const chord = normalizeChord(b.chord) ?? b.chord;
      const out: Array<{ readonly with: KeyBinding; readonly level: 'red' | 'yellow' }> = [];
      for (const e of ensure().byTrigger.get(trigger) ?? []) {
        if (e.when === null) continue;
        const same =
          e.binding.chord === chord && sameAction(e.binding, b) && whenKey(e.binding.when) === whenKey(b.when);
        if (same) continue;
        const level = conflictLevel({ binding: b, when: own }, { binding: e.binding, when: e.when }, fixed());
        if (level !== null) out.push({ with: e.binding, level });
      }
      return out.sort((x, y) => (x.level === y.level ? 0 : x.level === 'red' ? -1 : 1));
    },
    /**
     * Stores the overrides (canonical chords; invalid chords dropped). Chords refused on this OS are kept in the
     * settings (they may be valid on another OS the settings are synced to) but left out of the effective keymap
     * with a 'reserved' / 'ctrlAlt' issue.
     */
    setOverrides(o) {
      const normalized: KeyBinding[] = [];
      for (const b of o) {
        const chord = normalizeChord(b.chord);
        if (chord === null) {
          console.warn(`keymap: ignored override with invalid chord "${b.chord}"`);
          continue;
        }
        normalized.push(withChord(b, chord));
      }
      deps.settings.set('keyboard', 'overrides', normalized);
    },
    exportJson() {
      const kb = keyboard();
      return JSON.stringify(
        {
          format: KEYMAP_FORMAT,
          version: KEYMAP_VERSION,
          preset: kb.preset,
          altFree: kb.altFree,
          overrides: kb.overrides,
        },
        null,
        2,
      );
    },
    importJson(json) {
      const errors: string[] = [];
      let data: unknown;
      try {
        data = JSON.parse(json);
      } catch {
        return { ok: false, errors: [t(m.importNotJson)] };
      }
      let list: unknown;
      let preset: KeymapPresetId | undefined;
      let altFree: boolean | undefined;
      if (Array.isArray(data)) list = data;
      else if (isRecord(data) && data.format === KEYMAP_FORMAT) {
        const version = data.version;
        if (typeof version === 'number' && version > KEYMAP_VERSION) {
          return { ok: false, errors: [t(m.importNewerVersion, { version })] };
        }
        list = data.overrides ?? [];
        if (data.preset !== undefined) {
          if (data.preset === 'default' || data.preset === 'rpe') preset = data.preset;
          else errors.push(t(m.importBadPreset, { preset: JSON.stringify(data.preset) }));
        }
        if (data.altFree !== undefined) {
          if (typeof data.altFree === 'boolean') altFree = data.altFree;
          else errors.push(t(m.importBadAltFree));
        }
      } else return { ok: false, errors: [t(m.importWrongFormat)] };
      if (!Array.isArray(list)) return { ok: false, errors: [t(m.importWrongFormat)] };

      const overrides: KeyBinding[] = [];
      list.forEach((raw: unknown, i) => {
        const index = i + 1;
        if (!isRecord(raw) || typeof raw.chord !== 'string' || typeof raw.command !== 'string') {
          errors.push(t(m.importBadEntry, { index }));
          return;
        }
        const chord = normalizeChord(raw.chord);
        if (chord === null) {
          errors.push(t(m.importBadChord, { index, chord: raw.chord }));
          return;
        }
        if (deps.command !== undefined && deps.command(raw.command) === undefined) {
          errors.push(t(m.importUnknownCommand, { index, command: raw.command }));
          return;
        }
        const remove = raw.remove === true;
        if (!remove) {
          const c = check(chord);
          if (isRefused(c)) {
            errors.push(t(m.importRefused, { index, reason: service.explain(c) }));
            return;
          }
        }
        if (raw.args !== undefined && !isJsonValue(raw.args)) {
          errors.push(t(m.importBadEntry, { index }));
          return;
        }
        if (raw.when !== undefined) {
          if (typeof raw.when !== 'string') {
            errors.push(t(m.importBadEntry, { index }));
            return;
          }
          const w = compileWhen(raw.when, atomNames(), t);
          if ('error' in w) {
            errors.push(t(m.importBadWhen, { index, when: raw.when, error: w.error }));
            return;
          }
        }
        overrides.push({
          chord,
          command: raw.command,
          ...(raw.args === undefined ? {} : { args: raw.args }),
          ...(typeof raw.when === 'string' ? { when: raw.when } : {}),
          ...(remove ? { remove: true as const } : {}),
        });
      });
      // A file whose entries are all unusable (e.g. commands of another version) changes nothing at all, rather
      // than wiping the user's shortcuts.
      if (list.length > 0 && overrides.length === 0) {
        return { ok: false, errors: [...errors, t(m.importNothingUsable)] };
      }
      if (preset !== undefined) deps.settings.set('keyboard', 'preset', preset);
      if (altFree !== undefined) deps.settings.set('keyboard', 'altFree', altFree);
      deps.settings.set('keyboard', 'overrides', overrides);
      return { ok: errors.length === 0, errors };
    },
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    candidates,
    hasBindings(chord) {
      if (chord === null) return false;
      const trigger = triggerOf(chord);
      return trigger !== null && ensure().byTrigger.has(trigger);
    },
    entries: () => ensure().entries,
    eventChords: (e) => eventChords(e, os),
    wheelChord: (e) => wheelChord(e, os),
    pointerModifiers: (e) => pointerModifiers(e, os, keyboard().altFree),
    check,
    explain(c) {
      return c.status === 'ok' || c.status === 'invalid' ? '' : translateRef(t, c.reason);
    },
    normalize: normalizeChord,
    requires(entry, atom) {
      return entry.when !== null && whenRequires(entry.when.ast, atom, fixed());
    },
    issues: () => ensure().issues,
    dispose() {
      unsubSettings();
      unsubSources?.();
      unsubLabels();
      listeners.clear();
    },
  };
  return service;
}

function numpadChar(code: string): string {
  const rest = code.slice('Numpad'.length);
  const ops: Readonly<Record<string, string>> = {
    Add: '+',
    Subtract: '-',
    Multiply: '*',
    Divide: '/',
    Decimal: '.',
    Equal: '=',
    Comma: ',',
  };
  return ops[rest] ?? rest;
}

// ---------------------------------------------------------------------------------------------------------------
// Startup audit of the built-in presets (WORKPACKAGES rule 6; the Wave 2 gate fails on any error)
// ---------------------------------------------------------------------------------------------------------------

export interface AuditOptions {
  readonly command: (id: CommandId) => CommandDef | undefined;
  readonly whenAtomNames: ReadonlySet<string>;
  readonly translate?: Translate;
  /** Names used in issue texts (default: the chord as written and the translated command title). */
  readonly names?: IssueNames;
}

/** A wheel chord with Shift: macOS turns Shift+wheel into a horizontal scroll (deltaY 0), so it never arrives. */
export function isShiftWheelChord(chord: string): boolean {
  const p = parseChord(chord);
  return p?.kind === 'wheel' && p.shift;
}

/**
 * Checks every module binding of both presets: chord grammar, known command, valid condition, no reserved chord
 * (and a `mac` alternate for every chord in RESERVED_CHORDS_MAC), no Ctrl+Alt, an `altFree` declaration for every
 * Alt chord, a `mac` declaration for every Shift+wheel chord (DESIGN.md §8.1: nothing relies on Shift+wheel on a
 * Mac), valid alternates, and no red conflict for any combination of OS and Alt-free variant. Unreliable chords
 * are warnings.
 */
export function auditKeymaps(modules: readonly FeatureModule[], opts: AuditOptions): KeymapIssue[] {
  const issues: KeymapIssue[] = [];
  const t = opts.translate ?? defaultTranslate;
  const names: IssueNames = opts.names ?? {
    chord: (c) => c,
    command: (id) => {
      const title = opts.command(id)?.title;
      return title === undefined ? id : t(title);
    },
  };
  const describe = (b: KeyBinding): string => `${b.chord} -> ${b.command}${b.when === undefined ? '' : ` [${b.when}]`}`;

  for (const preset of KEYMAP_PRESETS) {
    for (const mod of modules) {
      for (const b of moduleBindings(mod, preset)) {
        const base = { preset, moduleId: mod.id, binding: b };
        const add = (
          level: KeymapIssue['level'],
          code: KeymapIssueCode,
          key: MessageKey,
          message: string,
          params?: Params,
        ): void => {
          issues.push({
            level,
            code,
            message: `${message}: ${describe(b)}`,
            text: issueText(names, key, b, params),
            ...base,
          });
        };
        const chord = normalizeChord(b.chord);
        if (chord === null) continue; // reported by assembleKeymap below
        if (opts.command(b.command) === undefined) {
          add('error', 'unknownCommand', m.issueUnknownCommand, 'unknown command');
        }
        const c = checkChord(chord, 'other');
        if (c.status === 'reserved') add('error', 'reserved', m.issueReserved, 'reserved chord');
        else if (c.status === 'ctrlAlt') add('error', 'ctrlAlt', m.issueCtrlAlt, 'Ctrl+Alt chord');
        else if (c.status === 'unreliable') add('warning', 'unreliable', m.issueUnreliable, 'unreliable chord');
        if (parseChord(chord)?.alt === true && b.altFree === undefined) {
          add('error', 'altFreeMissing', m.issueAltFreeMissing, 'Alt chord without altFree');
        }
        if (isMacReserved(chord) && b.mac === undefined) {
          add('error', 'macMissing', m.issueMacMissing, 'macOS-reserved chord without mac');
        }
        const altFreeShiftWheel =
          parseChord(chord)?.alt === true && typeof b.altFree === 'string' && isShiftWheelChord(b.altFree);
        if ((isShiftWheelChord(chord) || altFreeShiftWheel) && b.mac === undefined) {
          add('error', 'macShiftWheel', m.issueMacShiftWheel, 'Shift+wheel chord without mac');
        }
        for (const [field, alt] of [
          ['altFree', b.altFree],
          ['mac', b.mac],
        ] as const) {
          if (typeof alt !== 'string') continue;
          const n = normalizeChord(alt);
          const bad =
            n === null ||
            isRefused(checkChord(n, field === 'mac' ? 'mac' : 'other')) ||
            (field === 'altFree' && parseChord(n)?.alt === true) ||
            (field === 'mac' && isShiftWheelChord(n));
          if (bad) {
            add('error', 'badAlternate', m.issueBadAlternate, `invalid ${field} "${alt}"`, {
              alternate: n === null ? alt : names.chord(n),
            });
          }
        }
      }
    }

    for (const os of ['other', 'mac'] as const) {
      for (const altFree of [false, true]) {
        const compile = (b: KeyBinding): CompiledWhen | { readonly error: string } => {
          const own = compileWhen(b.when ?? '', opts.whenAtomNames, t);
          const cmdWhen = opts.command(b.command)?.when;
          if ('error' in own || cmdWhen === undefined) return own;
          const cmd = compileWhen(cmdWhen, opts.whenAtomNames, t);
          return 'error' in cmd ? cmd : compileAst(andWhen(own.ast, cmd.ast));
        };
        const { entries, issues: found } = assembleKeymap({
          modules,
          preset,
          altFree,
          os,
          overrides: [],
          compile,
          names,
        });
        if (os === 'other' && !altFree) issues.push(...found);
        const groups = new Map<Chord, KeymapEntry[]>();
        for (const e of entries) groups.set(e.trigger, [...(groups.get(e.trigger) ?? []), e]);
        for (const list of groups.values()) {
          for (let i = 0; i < list.length; i++) {
            for (let j = i + 1; j < list.length; j++) {
              const a = list[i];
              const b = list[j];
              if (a?.when == null || b?.when == null) continue;
              const level = conflictLevel(
                { binding: a.binding, when: a.when },
                { binding: b.binding, when: b.when },
                { preset, os },
              );
              if (level === 'red') {
                issues.push({
                  level: 'error',
                  code: 'conflict',
                  message: `red conflict (${preset}, ${os}${altFree ? ', Alt-free' : ''}): ${describe(a.binding)} vs ${describe(b.binding)}`,
                  text: issueText(names, m.issueConflict, a.binding, { other: names.command(b.binding.command) }),
                  preset,
                  os,
                  altFree,
                  moduleId: a.moduleId,
                  binding: a.binding,
                  with: b.binding,
                });
              }
            }
          }
        }
      }
    }
  }
  return issues;
}
